import { agentDisplayName } from "../ChatBox/agentName";
import React, { useCallback, useEffect, useState } from "react";
import { captureException, captureMessage } from "@sentry/react";
import { ChatToolType, FormatOptions } from "_graphql-types-frontend/graphql";
import { InsertDriveFileOutlined as FileOutlined, Description as FileTextOutlined, TableChartOutlined as TableOutlined, EditNote as FormOutlined, Download as DownloadOutlined, ExploreOutlined as CompassOutlined, Search as SearchOutlined, ShowChart as LineChartOutlined, MenuBookOutlined as BookOutlined, Api as ApiOutlined, Link as LinkOutlined, LightbulbOutlined as BulbOutlined, KeyboardArrowRight as RightOutlined, KeyboardArrowDown as DownOutlined } from "@mui/icons-material";
import {
  ApolloError,
  useApolloClient,
  useQuery,
  useSubscription,
} from "@apollo/client";
import { gql } from "_graphql-types-frontend";
import { Link } from "react-router-dom";

import { Box, styled } from "@mui/material";
import { BuildOutlined as ToolOutlined } from "@mui/icons-material";

import {
  McpToolCategory,
  McpToolName,
  MCP_TOOL_CATEGORY,
  MCP_TOOL_LABELS,
  type NavigationToolResult,
  type SearchToolResult,
  type DataToolResult,
  type DataToolPagination,
  type KnowledgeBaseToolResult,
  type SearchFieldKeysToolResult,
  type PublishedFieldsToolResult,
} from "sirius-shared/src/toolRegistry";
import { ENTITY_ROUTES } from "sirius-shared/src/routes";
import { ReportFile, useReportFile } from "../ReportFile/useReportFile";
import { CHAT_REPORT_FILES, CHAT_TASK_FINISHED, MY_SPAWNABLE_CHAT_AGENTS } from "./graphql";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

type RagSearchScope = {
  entityId?: number;
  entityType?: string;
  knowledgeBaseId?: number;
};

type DocumentsRagToolParams = {
  query: string;
  minDate?: number;
  maxDate?: number;
  entityId?: number;
  entityType?: string;
  knowledgeBaseId?: number;
};

type DocumentsRagToolResults = {
  searchScope?: RagSearchScope;
};

export const ToolIcon = () => {
  return <ToolOutlined style={{ fontSize: "0.875rem", color: "#666" }} />;
};

/** Build a human-readable label for the resolved RAG search scope. */
const scopeLabel = (scope?: RagSearchScope): string | null => {
  if (!scope) return null;
  if (scope.knowledgeBaseId) return `Knowledge Base #${scope.knowledgeBaseId}`;
  if (scope.entityType && scope.entityId)
    return `${scope.entityType} #${scope.entityId}`;
  return null;
};

/** Strip absolute URL prefix to get a path suitable for react-router Link */
const toRelativePath = (url: string): string => {
  try {
    const parsed = new URL(url);
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    // Already relative
    return url;
  }
};

// ---------------------------------------------------------------------------
// Built-in tool displays (existing)
// ---------------------------------------------------------------------------

const DocumentsRagTool = ({
  params,
  results,
}: {
  params: DocumentsRagToolParams;
  results?: DocumentsRagToolResults;
}) => {
  const dateQuery = [params.minDate, params.maxDate]
    .filter(year => {
      return year !== undefined;
    })
    .join(" - ");

  const scope = scopeLabel(results?.searchScope);

  return (
    <div
      data-cy="documents-rag-tool"
      style={{
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
      }}
    >
      <div>
        <FileOutlined />
        <span style={{ marginLeft: 5 }}>Document RAG</span>
        {scope && (
          <span style={{ marginLeft: 5, fontSize: 8, color: "gray" }}>
            ({scope})
          </span>
        )}
      </div>
      <div style={{ fontSize: 8, color: "gray" }}>
        {params.query} {dateQuery}
      </div>
    </div>
  );
};

type NotesRagToolParams = {
  query: string;
  "Min-Date"?: string;
  minDate?: string;
  entityId?: number;
  entityType?: string;
  knowledgeBaseId?: number;
};

type NotesRagToolResults = {
  searchScope?: RagSearchScope;
};

const NotesRagTool = ({
  params,
  results,
}: {
  params: NotesRagToolParams;
  results?: NotesRagToolResults;
}) => {
  const scope = scopeLabel(results?.searchScope);
  const minDate = params["Min-Date"] || params.minDate;

  return (
    <div
      data-cy="notes-rag-tool"
      style={{
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
      }}
    >
      <div>
        <FormOutlined />
        <span style={{ marginLeft: 5 }}>Note RAG</span>
        {scope && (
          <span style={{ marginLeft: 5, fontSize: 8, color: "gray" }}>
            ({scope})
          </span>
        )}
      </div>
      <div style={{ fontSize: 8, color: "gray" }}>{params.query}</div>
      {minDate && (
        <div
          style={{ fontSize: 8, color: "gray" }}
        >{`Min Date: ${minDate}`}</div>
      )}
    </div>
  );
};

// Mirrors CitationMetaData (entity-models LLMChat tool types) — the artifact
// the CitationTool emits, enriched with document context when entitled.
type CitationToolResults = {
  citationType: "documentExerpt" | "note" | "inlineDocument";
  citationId: number;
  indexStart?: number;
  indexEnd?: number;
  citationDescription: string;
  documentId?: number;
  /** Raw untrusted document text — render only via React escaping. */
  supportingText?: string;
  /** 1-based source page for the document-reader deep-link. */
  page?: number;
  /**
   * 1-based end of the source page range — only present when the cited chunk
   * spans pages and the exact quote could not be located server-side.
   */
  pageEnd?: number;
};

const CitationLabel = styled("span")(({ theme }) => ({
  color: theme.palette.grey[700],
  padding: "2px 8px",
  borderRadius: theme.shape.borderRadius,
  fontSize: "0.875rem",
  display: "inline-block",
}));

export const DOCUMENT_RANGER_CHAT_LINK = gql(/* GraphQL */ `
  query documentRangerChatLinkQuery($id: Int!) {
    document(id: $id) {
      id
      name
    }
  }
`);

const DocumentLink = ({
  documentId,
  supportingText,
  page,
  pageEnd,
}: {
  documentId: number;
  supportingText: string;
  page?: number;
  pageEnd?: number;
}) => {
  const { data, loading } = useQuery(DOCUMENT_RANGER_CHAT_LINK, {
    variables: { id: documentId },
  });

  if (loading) {
    return "loading...";
  }
  // Deep-link to the source page (when known) and the cited excerpt so the
  // reader jumps to the exact page and highlights the quote there. Cap quote
  // length — overly long quotes risk hitting URL limits and the reader falls
  // back to a prefix search anyway. When the quote could not be located
  // server-side, pageEnd widens the target to a page range instead.
  const params = new URLSearchParams();
  if (supportingText) params.set("quote", supportingText.slice(0, 300));
  if (page) params.set("page", String(page));
  if (page && pageEnd && pageEnd > page) params.set("pageEnd", String(pageEnd));
  const queryString = params.toString();
  const href = queryString
    ? `/documents/${documentId}?${queryString}`
    : `/documents/${documentId}`;
  return (
    <a href={href} target="_blank">
      {loading ? "Loading" : data?.document?.name}
    </a>
  );
};

const CitationTool = ({ results }: { results: CitationToolResults }) => {
  const {
    citationType,
    citationId,
    citationDescription,
    documentId,
    supportingText,
    page,
    pageEnd,
  } = results;

  if (citationType === "documentExerpt" && documentId) {
    return (
      <DocumentLink
        documentId={documentId}
        supportingText={supportingText || ""}
        page={page}
        pageEnd={pageEnd}
      />
    );
  }

  // Note citations deep-link to the notes page filtered to the cited note;
  // GlobalNotes reads the noteId param into an `ids` filter so it loads
  // reliably regardless of paging.
  if (citationType === "note" && citationId) {
    return (
      <a href={`/notes?noteId=${citationId}`} target="_blank" rel="noreferrer">
        {citationDescription || "View note"}
      </a>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "0.75rem",
        color: "gray",
      }}
    >
      Citation Ref. {citationDescription}
    </Box>
  );
};

type CsvExportToolResults = {
  success: boolean;
  downloadUrl: string;
  filename: string;
  rowCount: number;
  columnCount: number;
  expiresIn: string;
  message: string;
};

const CsvExportTool = ({ results }: { results: CsvExportToolResults }) => {
  if (!results || !results.downloadUrl) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.875rem",
          color: "orange",
        }}
      >
        <DownloadOutlined style={{ fontSize: "0.875rem" }} />
        CSV export in progress or missing download URL...
        {results && <pre>{JSON.stringify(results, null, 2)}</pre>}
      </Box>
    );
  }

  return (
    <Box
      component="a"
      href={results.downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        fontSize: "0.875rem",
        color: "primary.main",
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline",
        },
      }}
    >
      <DownloadOutlined style={{ fontSize: "0.875rem" }} />
      {results.filename} ({results.rowCount} rows, {results.columnCount} cols)
    </Box>
  );
};

type DiligenceReportResults = {
  taskId: string;
  reportType: "caseStudy" | "onePager";
  investment: { id: number; name: string };
  /** The chat the finished file is saved to; absent on older results. */
  conversationId?: number | null;
};

const DILIGENCE_REPORTS: Record<
  DiligenceReportResults["reportType"],
  { label: string; file: ReportFile }
> = {
  caseStudy: { label: "case study", file: { kind: "caseStudy" } },
  onePager: {
    label: "one pager",
    file: { kind: "investmentReport", format: FormatOptions.Docx },
  },
};

// How often an unfinished report card looks again while it waits on the push.
const REPORT_RECHECK_MS = 30_000;

/** A generateDiligenceReport call whose job has been queued. */
const isDiligenceReportCall = (t: {
  tool: string;
  toolName?: string | null;
  results?: any;
}) =>
  t.tool === ChatToolType.SiriusMcp &&
  t.toolName === McpToolName.GenerateDiligenceReport &&
  !!t.results?.taskId;

/**
 * A report started from the chat, from "generating" to a Download link.
 *
 * Works from the task id saved in the tool result, so a reloaded conversation
 * picks up where it left off: one look on mount shows a report that finished
 * while the chat was closed. While it runs, the worker's push says when it is
 * done, and a slow re-check covers a push that never arrives (the socket
 * client reconnects silently, so a lost push raises no error); polling every
 * two seconds stands in when the subscription itself fails. Once done, the
 * link is to the copy saved in the conversation, which outlives the temp file
 * the report query links to (that one is only the fallback, for a report that
 * was not saved). The link is signed on each look and never saved — it expires.
 */
const DiligenceReportDisplay = ({
  results,
}: {
  results: DiligenceReportResults;
}) => {
  const { taskId, reportType, investment, conversationId } = results;
  const { label, file } = DILIGENCE_REPORTS[reportType];
  const client = useApolloClient();
  const { fetchUrl, waitForUrl } = useReportFile();
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [pushUnavailable, setPushUnavailable] = useState(false);
  const finished = !!url || failed;

  /** Show a finished report, by the conversation's own copy when it has one. */
  const showDone = useCallback(
    async (reportUrl: string) => {
      if (!conversationId) {
        setUrl(reportUrl);
        return;
      }
      try {
        const { data } = await client.query({
          query: CHAT_REPORT_FILES,
          variables: { conversationId },
          fetchPolicy: "network-only",
        });
        const saved = data.conversationArtifacts.find(
          a => a.metadata?.taskId === taskId
        );
        setUrl(saved?.downloadUrl ?? reportUrl);
      } catch (error) {
        // The report is done either way; its own link still works.
        captureException(error);
        setUrl(reportUrl);
      }
    },
    [client, conversationId, taskId]
  );

  const check = useCallback(() => {
    fetchUrl(taskId, file).then(
      found => (found ? showDone(found) : undefined),
      error => {
        // Only the task's own failure (a GraphQL error from the report query)
        // is "failed". A dropped request is not; the next look retries it.
        if (error instanceof ApolloError && error.graphQLErrors.length) {
          setFailed(true);
        }
      }
    );
  }, [fetchUrl, taskId, file, showDone]);

  useEffect(() => {
    check();
  }, [check]);

  useSubscription(CHAT_TASK_FINISHED, {
    variables: { taskId },
    skip: finished,
    onData: () => check(),
    onError: () => setPushUnavailable(true),
    onComplete: () => setPushUnavailable(true),
  });

  useEffect(() => {
    if (finished || pushUnavailable) return;
    const timer = setInterval(check, REPORT_RECHECK_MS);
    return () => clearInterval(timer);
  }, [finished, pushUnavailable, check]);

  useEffect(() => {
    if (finished || !pushUnavailable) return;
    const controller = new AbortController();
    waitForUrl(taskId, file, controller.signal)
      .then(showDone)
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [finished, pushUnavailable, waitForUrl, taskId, file, showDone]);

  if (url) {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-cy="diligence-report-download"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          my: 0.5,
          fontSize: "0.875rem",
          color: "primary.main",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        <DownloadOutlined style={{ fontSize: "0.875rem" }} />
        Download the {label} for {investment.name}
      </Box>
    );
  }
  if (failed) {
    return (
      <Box
        component="span"
        data-cy="diligence-report-failed"
        sx={{ display: "inline-flex", my: 0.5, fontSize: "0.875rem", color: "error.main" }}
      >
        The {label} for {investment.name} failed. The notification bell has the details.
      </Box>
    );
  }
  return (
    <Box
      component="span"
      data-cy="diligence-report-generating"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        my: 0.5,
        fontSize: "0.875rem",
        color: "gray",
      }}
    >
      <FileOutlined style={{ fontSize: "0.875rem" }} />
      Generating the {label} for {investment.name}…
    </Box>
  );
};

type AtlasToolParams = {
  command: string;
  script?: string;
  scriptName?: string;
  inputFiles?: { bucket: string; key: string; name: string }[];
  outputFileNames?: string[];
};

type AtlasToolResults = {
  exitCode: number;
  stdout?: string;
  stderr?: string;
  outputFiles?: { name: string; downloadUrl: string }[];
};

const AtlasToolDisplay = ({
  params,
  results,
}: {
  params: AtlasToolParams;
  results?: AtlasToolResults;
}) => {
  const [scriptExpanded, setScriptExpanded] = useState(false);

  if (!params) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <ApiOutlined style={{ fontSize: "0.875rem", color: "#666" }} />
        <span>Atlas (no parameters)</span>
      </Box>
    );
  }

  const hasScript = params?.script;
  const hasInputFiles = params?.inputFiles && params.inputFiles.length > 0;
  const hasOutputFiles = results?.outputFiles && results.outputFiles.length > 0;
  const command = params?.command || "python script";
  const scriptName = params?.scriptName || "script.py";

  if (!results) {
    // Loading state - show params only
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <ApiOutlined style={{ fontSize: "0.875rem", color: "#666" }} />
        <span>Atlas executing...</span>
        {hasScript && <span style={{ color: "#999" }}>({scriptName})</span>}
        {hasInputFiles && (
          <span style={{ color: "#999" }}>
            ← {params.inputFiles!.length} input
            {params.inputFiles!.length > 1 ? "s" : ""}
          </span>
        )}
      </Box>
    );
  }

  const success = results.exitCode === 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        fontSize: "0.75rem",
      }}
    >
      {/* Header line */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          color: success ? "gray" : "error.main",
        }}
      >
        <ApiOutlined style={{ fontSize: "0.875rem" }} />
        <span style={{ fontWeight: 500 }}>
          Atlas {success ? "✓" : `✗ (exit ${results.exitCode})`}
        </span>
        {hasScript && <span style={{ color: "#999" }}>({scriptName})</span>}
        {hasInputFiles && (
          <span style={{ color: "#999" }}>
            ← {params.inputFiles!.length} input
            {params.inputFiles!.length > 1 ? "s" : ""}
          </span>
        )}
        {hasOutputFiles && (
          <span style={{ color: "#999" }}>
            → {results.outputFiles!.length} output
            {results.outputFiles!.length > 1 ? "s" : ""}
          </span>
        )}
      </Box>

      {/* Command */}
      <Box
        sx={{
          fontFamily: "monospace",
          fontSize: "0.7rem",
          color: "#666",
          backgroundColor: "#f5f5f5",
          padding: "2px 6px",
          borderRadius: "3px",
        }}
      >
        $ {command}
      </Box>

      {/* Collapsible script viewer */}
      {hasScript && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Box
            onClick={() => setScriptExpanded(prev => !prev)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              fontSize: "0.7rem",
              color: "gray",
              userSelect: "none",
              "&:hover": { color: "#333" },
            }}
          >
            {scriptExpanded ? (
              <DownOutlined style={{ fontSize: "0.5rem" }} />
            ) : (
              <RightOutlined style={{ fontSize: "0.5rem" }} />
            )}
            <FileTextOutlined style={{ fontSize: "0.7rem" }} />
            <span>{scriptName}</span>
            <span style={{ color: "#aaa" }}>
              ({params.script!.split("\n").length} lines)
            </span>
          </Box>
          {scriptExpanded && (
            <Box
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.7rem",
                color: "#333",
                backgroundColor: "#fafafa",
                border: "1px solid #e0e0e0",
                padding: "6px 10px",
                borderRadius: "3px",
                whiteSpace: "pre-wrap",
                maxHeight: "400px",
                overflowY: "auto",
                margin: 0,
              }}
            >
              {params.script}
            </Box>
          )}
        </Box>
      )}

      {/* Output files with download links */}
      {hasOutputFiles && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mt: 0.5 }}>
          {results.outputFiles!.map(file => (
            <Box
              key={file.name}
              component="a"
              href={file.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.75rem",
                color: "primary.main",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <DownloadOutlined style={{ fontSize: "0.75rem" }} />
              {file.name}
            </Box>
          ))}
        </Box>
      )}

      {/* Show stdout/stderr if present and non-trivial */}
      {results.stdout && results.stdout.trim().length > 0 && (
        <Box
          sx={{
            fontFamily: "monospace",
            fontSize: "0.7rem",
            color: "#333",
            backgroundColor: "#f9f9f9",
            padding: "4px 8px",
            borderRadius: "3px",
            whiteSpace: "pre-wrap",
            maxHeight: "120px",
            overflowY: "auto",
          }}
        >
          {results.stdout.trim()}
        </Box>
      )}
      {results.stderr && results.stderr.trim().length > 0 && (
        <Box
          sx={{
            fontFamily: "monospace",
            fontSize: "0.7rem",
            color: "#d32f2f",
            backgroundColor: "#ffebee",
            padding: "4px 8px",
            borderRadius: "3px",
            whiteSpace: "pre-wrap",
            maxHeight: "120px",
            overflowY: "auto",
          }}
        >
          {results.stderr.trim()}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// MCP tool displays
// ---------------------------------------------------------------------------

/** Renders a clickable internal navigation link */
const InternalLink = ({ url, label }: { url: string; label: string }) => (
  <Link
    to={toRelativePath(url)}
    style={{
      color: "#0091F9", // theme primary ($blue-500)
      textDecoration: "none",
      fontSize: "0.75rem",
    }}
  >
    <LinkOutlined style={{ marginRight: 4, fontSize: "0.625rem" }} />
    {label}
  </Link>
);

/** Resolves a human-readable label for an MCP tool name */
const mcpLabel = (toolName?: string | null): string => {
  if (!toolName) return "MCP Tool";
  return MCP_TOOL_LABELS[toolName as McpToolName] || toolName;
};

/** Navigation tool display: clickable link with title */
const McpNavigationDisplay = ({
  toolName,
  results,
}: {
  toolName?: string | null;
  results: any;
}) => {
  const label = mcpLabel(toolName);
  const nav = results as NavigationToolResult | undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <CompassOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        {label}
      </Box>
      {nav?.url && (
        <Link
          to={toRelativePath(nav.url)}
          style={{
            color: "#0091F9", // theme primary ($blue-500)
            textDecoration: "none",
            fontSize: "0.8125rem",
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <LinkOutlined style={{ fontSize: "0.75rem" }} />
          {nav.title || label}
        </Link>
      )}
    </Box>
  );
};

/** Search tool display: query, result count, clickable entity links */
const McpSearchDisplay = ({
  toolName,
  params,
  results,
}: {
  toolName?: string | null;
  params: any;
  results: any;
}) => {
  const label = mcpLabel(toolName);
  const searchName = params?.name || "";
  const hasError =
    results != null && typeof results === "object" && "error" in results;
  const search = hasError
    ? undefined
    : (results as SearchToolResult | undefined);
  // results is {} (empty object) on initial toolStart publish before tool completes
  const pending = !hasError && (!results || Object.keys(results).length === 0);
  const count = search?.count ?? 0;
  const items = (search?.results || []).slice(0, 5);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <SearchOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        {label}: &quot;{searchName}&quot;{" "}
        {hasError ? (
          <span style={{ color: "#d32f2f" }}>(error)</span>
        ) : pending ? (
          <span style={{ color: "#999" }}>(searching...)</span>
        ) : (
          <>
            ({count} result{count !== 1 ? "s" : ""})
          </>
        )}
      </Box>
      {items.map(item => {
        const type =
          item.type ||
          (toolName || "")
            .replace("search", "")
            .replace(/s$/i, "")
            .toLowerCase();
        const routeFn =
          ENTITY_ROUTES[type as keyof typeof ENTITY_ROUTES] || null;
        const url =
          typeof routeFn === "function" ? (routeFn as Function)(item.id) : "";

        return (
          <Box
            key={`${type}-${item.id}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: "0.625rem",
              ml: 2,
            }}
          >
            {url ? (
              <InternalLink url={url} label={item.name} />
            ) : (
              <span>{item.name}</span>
            )}
            {item.type && (
              <span
                style={{
                  marginLeft: 4,
                  color: "#999",
                  fontSize: "0.625rem",
                }}
              >
                ({item.type})
              </span>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

/** Data tool display: tool label, entity name, navigation links */
const McpDataDisplay = ({
  toolName,
  params,
  results,
}: {
  toolName?: string | null;
  params?: any;
  results: any;
}) => {
  const label = mcpLabel(toolName);
  const pending = !results || Object.keys(results).length === 0;
  const data = results as Partial<DataToolResult> | undefined;
  const investmentName = pending
    ? undefined
    : data?.investmentName ||
      (data?.investmentId ? `Investment ${data.investmentId}` : undefined);
  const navigation = data?.navigation;
  const filterHint = summarizeFilterParams(params);
  const pagination = data?.pagination as DataToolPagination | undefined;
  const paginationHint =
    pagination && pagination.totalPages > 0
      ? `Page ${pagination.page} of ${pagination.totalPages}`
      : null;
  const subtitle = [filterHint, paginationHint].filter(Boolean).join(" · ");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <LineChartOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        {label}
        {investmentName ? `: ${investmentName}` : ""}
      </Box>
      {subtitle && (
        <Box
          component="span"
          sx={{
            fontSize: "0.7rem",
            color: "#999",
            pl: "1.125rem",
          }}
        >
          {subtitle}
        </Box>
      )}
      {navigation?.primary?.url && (
        <InternalLink
          url={navigation.primary.url}
          label={navigation.primary.label || "View details"}
        />
      )}
      {navigation?.related?.map(link =>
        link.url ? (
          <InternalLink
            key={`${link.url}-${link.label}`}
            url={link.url}
            label={link.label}
          />
        ) : null
      )}
    </Box>
  );
};

/** Knowledge base tool display */
const McpKnowledgeBaseDisplay = ({ results }: { results: any }) => {
  const pending = !results || Object.keys(results).length === 0;
  const kb = results as Partial<KnowledgeBaseToolResult> | undefined;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <BookOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        {kb?.name || "Knowledge Base"}
      </Box>
      {!pending && (
        <Box
          component="span"
          sx={{ fontSize: "0.625rem", color: "gray", ml: 2 }}
        >
          {kb?.documentCount ?? 0} docs, {kb?.noteCount ?? 0} notes
          {kb?.status && ` \u2014 ${kb.status}`}
        </Box>
      )}
    </Box>
  );
};

/** Search field keys display: shows query and matching field count */
const McpSearchFieldKeysDisplay = ({
  params,
  results,
}: {
  params: any;
  results: any;
}) => {
  const pending = !results || Object.keys(results).length === 0;
  const data = results as Partial<SearchFieldKeysToolResult> | undefined;
  const question = params?.question || "";
  const count = data?.count ?? 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <SearchOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        Search Field Keys: &quot;{question}&quot;{" "}
        {pending ? (
          <span style={{ color: "#999" }}>(searching...)</span>
        ) : (
          <>
            ({count} field{count !== 1 ? "s" : ""} found)
          </>
        )}
      </Box>
      {!pending && data?.fieldKeys && data.fieldKeys.length > 0 && (
        <>
          {data.fieldKeys.slice(0, 5).map(fk => (
            <Box
              key={fk.fieldKey}
              sx={{
                fontSize: "0.625rem",
                color: "#666",
                ml: 2,
              }}
            >
              {fk.displayName || fk.fieldKey}
              {fk.category && (
                <span style={{ color: "#999", marginLeft: 4 }}>
                  ({fk.category})
                </span>
              )}
            </Box>
          ))}
          {data.fieldKeys.length > 5 && (
            <Box sx={{ fontSize: "0.625rem", color: "#999", ml: 2 }}>
              +{data.fieldKeys.length - 5} more
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

/** Build a short description of what was searched for */
const describeFieldSearch = (params: any, data: Partial<PublishedFieldsToolResult> | undefined): string | null => {
  const parts: string[] = [];

  if (data?.knowledgeBaseName) {
    parts.push(`KB: ${data.knowledgeBaseName}`);
  } else if (params?.knowledgeBaseId) {
    parts.push(`KB #${params.knowledgeBaseId}`);
  }

  if (params?.semanticQuery) {
    parts.push(`"${params.semanticQuery}"`);
  } else if (params?.text) {
    parts.push(`"${params.text}"`);
  }

  if (params?.fieldKeys?.length) {
    const keys = params.fieldKeys as string[];
    parts.push(keys.length <= 3 ? keys.join(", ") : `${keys.length} field keys`);
  }

  // Entity context
  const ownerName = data?.fields?.[0]?.ownerName;
  if (ownerName) {
    parts.push(ownerName);
  } else if (params?.investmentIds?.length || params?.firmIds?.length) {
    const total = (params.investmentIds?.length ?? 0) + (params.firmIds?.length ?? 0);
    parts.push(`${total} entit${total === 1 ? "y" : "ies"}`);
  } else if (params?.investmentId) {
    parts.push(`Investment ${params.investmentId}`);
  } else if (params?.firmId) {
    parts.push(`Firm ${params.firmId}`);
  }

  return parts.length > 0 ? parts.join(" \u2014 ") : null;
};

/** Published fields display: shows search context and field count */
const McpPublishedFieldsDisplay = ({
  params,
  results,
}: {
  params: any;
  results: any;
}) => {
  const pending = !results || Object.keys(results).length === 0;
  const data = results as Partial<PublishedFieldsToolResult> | undefined;
  const count = data?.count ?? 0;
  const hasMore = data?.hasMore ?? false;
  const searchDesc = describeFieldSearch(params, data);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <BookOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        Published Fields
        {" "}
        {pending ? (
          <span style={{ color: "#999" }}>(loading...)</span>
        ) : (
          <>
            ({count} field{count !== 1 ? "s" : ""}
            {hasMore ? ", more available" : ""})
          </>
        )}
      </Box>
      {searchDesc && (
        <Box
          component="span"
          sx={{ fontSize: "0.625rem", color: "#666", ml: 2 }}
        >
          {searchDesc}
        </Box>
      )}
    </Box>
  );
};

/** Summarize tool params into a short description string */
const formatParamValue = (val: unknown): string => {
  if (typeof val === "string")
    return val.length > 40 ? val.slice(0, 40) + "…" : val;
  if (Array.isArray(val)) return `[${val.length} items]`;
  return String(val);
};

const summarizeParams = (params: any): string | null => {
  if (!params || typeof params !== "object") return null;
  const keys = Object.keys(params).filter(
    k => params[k] !== undefined && params[k] !== null && params[k] !== ""
  );
  if (keys.length === 0) return null;
  // Show first meaningful param value as a hint
  const first = keys[0];
  return `${first}: ${formatParamValue(params[first])}`;
};

/** Show all non-trivial filter params (excluding investmentId which is shown in the label) */
const summarizeFilterParams = (params: any): string | null => {
  if (!params || typeof params !== "object") return null;
  const skip = new Set(["investmentId", "page", "pageSize"]);
  const entries = Object.entries(params).filter(
    ([k, v]) => !skip.has(k) && v !== undefined && v !== null && v !== ""
  );
  if (entries.length === 0) return null;
  return entries
    .map(([k, v]) => `${k}: ${formatParamValue(v)}`)
    .join(", ");
};

/** Generic fallback for unknown/unmapped MCP tools — shows label, params hint, and urls */
const McpGenericDisplay = ({
  toolName,
  params,
  results,
}: {
  toolName?: string | null;
  params?: any;
  results?: any;
}) => {
  const urls: string[] | undefined = results?.urls;
  const paramHint = summarizeParams(params);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <ApiOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        {mcpLabel(toolName)}
        {paramHint && (
          <span style={{ marginLeft: 4, color: "#999" }}>({paramHint})</span>
        )}
      </Box>
      {urls &&
        urls.length > 0 &&
        urls.map((url, i) => (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0091F9", // theme primary ($blue-500)
              textDecoration: "none",
              fontSize: "0.75rem",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <LinkOutlined style={{ marginRight: 4, fontSize: "0.625rem" }} />
            {url.length > 60 ? url.slice(0, 60) + "…" : url}
          </a>
        ))}
    </Box>
  );
};

/** Format Alpha Vantage tool names for display (e.g. "GLOBAL_QUOTE" → "Global Quote") */
const formatAvToolName = (name: string): string =>
  name
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

/** Alpha Vantage market data tool display — generic for any remote MCP tool */
const AlphaVantageMcpTool = ({
  toolName,
  results,
}: {
  toolName?: string | null;
  results: any;
}) => {
  const pending = !results || Object.keys(results).length === 0;
  const label = toolName ? formatAvToolName(toolName) : "Market Data";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          fontSize: "0.75rem",
          color: "gray",
        }}
      >
        <LineChartOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
        {label}
        {pending ? " (loading...)" : ""}
      </Box>
    </Box>
  );
};

/** Dispatch MCP tool to the appropriate sub-renderer based on category */
const SiriusMcpTool = ({
  toolName,
  params,
  results,
}: {
  toolName?: string | null;
  params: any;
  results: any;
}) => {
  // Handle tools with dedicated renderers first
  if (toolName === McpToolName.SearchFieldKeys) {
    return <McpSearchFieldKeysDisplay params={params} results={results} />;
  }
  if (toolName === McpToolName.GetPublishedFields) {
    return <McpPublishedFieldsDisplay params={params} results={results} />;
  }
  if (toolName === McpToolName.StageMutationPlan || toolName === ChatToolType.StageMutationsTool) {
    return <StageMutationsToolDisplay params={params} results={results} />;
  }

  const category = toolName
    ? MCP_TOOL_CATEGORY[toolName as McpToolName]
    : undefined;

  switch (category) {
    case McpToolCategory.Navigation:
      return <McpNavigationDisplay toolName={toolName} results={results} />;
    case McpToolCategory.Search:
      return (
        <McpSearchDisplay
          toolName={toolName}
          params={params}
          results={results}
        />
      );
    case McpToolCategory.Data:
      return <McpDataDisplay toolName={toolName} params={params} results={results} />;
    case McpToolCategory.KnowledgeBase:
      return <McpKnowledgeBaseDisplay results={results} />;
    case McpToolCategory.Mutation:
      return <McpGenericDisplay toolName={toolName} params={params} results={results} />;
    default:
      return (
        <McpGenericDisplay
          toolName={toolName}
          params={params}
          results={results}
        />
      );
  }
};

// ---------------------------------------------------------------------------
// SpawnSubAgentTool display
// ---------------------------------------------------------------------------

type SpawnSubAgentToolParams = {
  task: string;
  model?: string;
  tools?: string[];
  parentContext?: string;
  /** Name of the configured agent this spawn requested (issue 5224). */
  agent?: string;
  mode?: string;
};

type SubAgentToolCall = {
  tool: string;
  toolName?: string;
  params: any;
  results: any;
};

type SpawnSubAgentToolResults = {
  subAgentResult?: string;
  model?: string;
  /** The effort actually sent — absent when the model default ran. */
  effort?: string;
  tools?: SubAgentToolCall[];
  error?: string;
  /** The agent that actually answered — authoritative, server-side. */
  agent?: string;
  mode?: string;
  /** The persistent thread this run belongs to, if any. */
  threadConversationId?: number;
  /** True when this run continued an existing thread rather than opening one. */
  resumedThread?: boolean;
};

const SpawnSubAgentToolDisplay = ({
  params,
  results,
}: {
  params: SpawnSubAgentToolParams;
  results?: SpawnSubAgentToolResults;
}) => {
  const [expanded, setExpanded] = useState(false);
  const pending = !results;
  const hasError = results && "error" in results && results.error;
  // Prefer the server's answer over the request: `results.agent` is what
  // actually ran, `params.agent` is only what was asked for (and is all we
  // have while the run is still pending).
  const agentName = results?.agent ?? params?.agent;
  // While a configured agent runs, its stored model and effort are what the
  // server runs it on (they override the request's), so show those until the
  // server answers. The agents list is usually cached by the agents tab.
  const { data: agentsData } = useQuery<{
    mySpawnableChatAgents: { name: string; model: string | null; effort: string | null }[];
  }>(MY_SPAWNABLE_CHAT_AGENTS, { fetchPolicy: "cache-first", skip: !pending || !params?.agent });
  const configured = agentsData?.mySpawnableChatAgents.find(a => a.name === params?.agent);
  // Server's effort once it answers; the request's may have been overridden by the agent.
  // The agents list spells efforts as the enum does ("XHigh"); the server, lowercase.
  const effort = results?.effort ?? (pending ? configured?.effort?.toLowerCase() : undefined);
  // The server's model once it answers. While pending, a configured agent's
  // stored model, or the request's model for an ad-hoc child; with neither the
  // server picks, so no name is shown rather than a guess.
  const model = results?.model ?? (params?.agent ? configured?.model ?? undefined : params?.model);
  const modelLabel = model ? ` (${model}${effort ? ` · ${effort}` : ""})` : "";
  const subTools = results?.tools ?? [];
  const resumed = results?.resumedThread === true;
  const persistent =
    (results?.mode ?? params?.mode) === "persistent" || resumed;

  const statusColor = hasError
    ? "error.main"
    : pending
      ? "#1976d2"
      : "#2e7d32";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, fontSize: "0.75rem" }}>
      <Box
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          color: statusColor,
          userSelect: "none",
          transition: "color 0.3s ease",
          "&:hover": { color: "#333" },
        }}
      >
        {expanded ? (
          <DownOutlined style={{ fontSize: "0.5rem" }} />
        ) : (
          <RightOutlined style={{ fontSize: "0.5rem" }} />
        )}
        <ApiOutlined style={{ fontSize: "0.875rem" }} />
        <span style={{ fontWeight: 500 }}>
          {/* A configured agent answers under its own name; an ad-hoc child
              keeps the generic label so existing transcripts read unchanged. */}
          {agentName ? `${agentDisplayName(agentName)}${modelLabel}` : `Sub-agent${modelLabel}`}
          {pending && " — working..."}
          {hasError && " — failed"}
          {results && !hasError && " — done"}
        </span>
        {persistent && (
          <span
            style={{ color: "#999", fontWeight: 400 }}
            title={
              resumed
                ? "Continued this agent's ongoing thread — it can see what it said earlier"
                : "Started this agent's ongoing thread — later turns can build on it"
            }
          >
            · {resumed ? "continuing thread" : "new thread"}
          </span>
        )}
        {subTools.length > 0 && (
          <span style={{ color: "#999", fontWeight: 400 }}>
            · {subTools.length} tool{subTools.length !== 1 ? "s" : ""} used
          </span>
        )}
      </Box>
      <Box sx={{ fontSize: "0.7rem", color: "#666", pl: "1.125rem" }}>
        {params?.task
        ? params.task.length > 100 ? params.task.slice(0, 100) + "…" : params.task
        : "Sub-agent task"}
      </Box>
      {expanded && subTools.length > 0 && (
        <Box sx={{ ml: "1.125rem", borderLeft: "2px solid #e0e0e0", pl: 1, mt: 0.5 }}>
          {subTools.map((t, i) => (
            <ToolDisplays
              key={`sub-${i}`}
              tool={t.tool as ChatToolType}
              toolName={t.toolName}
              params={t.params}
              results={t.results}
            />
          ))}
        </Box>
      )}
      {/* A persistent agent's reply is lifted into the transcript as its own
          message (agentSpeakers.tsx); repeating it here would show it twice. */}
      {expanded && results && !hasError && results.subAgentResult && persistent && (
        <Box
          data-testid="subagent-replied-below"
          sx={{ fontSize: "0.7rem", color: "text.secondary", ml: "1.125rem" }}
        >
          Replied in the chat below.
        </Box>
      )}
      {expanded && results && !hasError && results.subAgentResult && !persistent && (
        <Box
          sx={{
            fontFamily: "monospace",
            fontSize: "0.7rem",
            color: "#333",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            padding: "6px 10px",
            borderRadius: "3px",
            whiteSpace: "pre-wrap",
            maxHeight: "200px",
            overflowY: "auto",
            ml: "1.125rem",
          }}
        >
          {results.subAgentResult}
        </Box>
      )}
      {expanded && hasError && (
        <Box
          sx={{
            fontSize: "0.7rem",
            color: "#d32f2f",
            backgroundColor: "#ffebee",
            padding: "4px 8px",
            borderRadius: "3px",
            ml: "1.125rem",
          }}
        >
          {results.error}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// StageMutationsTool display
// ---------------------------------------------------------------------------

const BetaChip = () => (
  <span style={{
    fontSize: "0.5rem",
    fontWeight: 600,
    color: "#7c4dff",
    border: "1px solid #7c4dff",
    borderRadius: 3,
    padding: "0 3px",
    marginLeft: 4,
    verticalAlign: "middle",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }}>
    beta
  </span>
);

const StageMutationsToolDisplay = ({
  params,
  results,
}: {
  params: { title: string; steps: Array<{ mutationName: string; description: string }> };
  results?: { planId: number; status: string; title: string } | { error: string };
}) => {
  if (results && "error" in results) {
    return (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.75rem", color: "red" }}>
        <ToolIcon /> <span>Data update plan failed: {results.error}</span>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", fontSize: "0.75rem", color: "gray" }}>
      <div>
        <FormOutlined />
        <span style={{ marginLeft: 5 }}>Staged Data Updates</span>
        <BetaChip />
        {results && "planId" in results && (
          <span style={{ marginLeft: 5, fontSize: 8 }}>(#{results.planId})</span>
        )}
      </div>
      <div style={{ fontSize: 8 }}>
        {params.title} — {params.steps?.length ?? 0} step(s)
      </div>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main ToolDisplays component
// ---------------------------------------------------------------------------

export const ToolDisplays = (props:
  | {
      tool: ChatToolType.DocumentsRagTool;
      toolName?: string | null;
      params: DocumentsRagToolParams;
      results: DocumentsRagToolResults | undefined;
    }
  | {
      tool: ChatToolType.NotesRagTool;
      toolName?: string | null;
      params: NotesRagToolParams;
      results: NotesRagToolResults | undefined;
    }
  | {
      tool: ChatToolType.CitationTool;
      toolName?: string | null;
      params: null;
      results: CitationToolResults;
    }
  | {
      tool: ChatToolType.CurrentPageTool;
      toolName?: string | null;
      params: null;
      results: null;
    }
  | {
      tool: ChatToolType.CsvExportTool;
      toolName?: string | null;
      params: { data: any[]; filename: string };
      results: CsvExportToolResults;
    }
  | {
      tool: ChatToolType.AlphaVantageMcp;
      toolName?: string | null;
      params: any;
      results: any;
    }
  | {
      tool: ChatToolType.SiriusMcp;
      toolName?: string | null;
      params: any;
      results: any;
    }
  | {
      tool: ChatToolType.UpdatePreferencesTool;
      toolName?: string | null;
      params: { updatedPreferences: string };
      results: { saved: boolean; preferences: string };
    }
  | {
      tool: ChatToolType.DocumentTextReaderTool;
      toolName?: string | null;
      params: {
        fileName: string;
        mode?: string;
        query?: string;
        offset?: number;
        limit?: number;
      };
      results: any;
    }
  | {
      tool: ChatToolType.SpreadsheetReaderTool;
      toolName?: string | null;
      params: {
        fileName: string;
        sheetName?: string;
        rowStart?: number;
        rowEnd?: number;
      };
      results: any;
    }
  | {
      tool: ChatToolType.AtlasTool;
      toolName?: string | null;
      params: AtlasToolParams;
      results?: AtlasToolResults;
    }
  | {
      tool: ChatToolType.SpawnSubAgentTool;
      toolName?: string | null;
      // When toolName is set this is a sub-agent's internal MCP tool call
      // (rendered via SiriusMcpTool), so params/results are arbitrary MCP
      // shapes — matching the other MCP members of this union.
      params: SpawnSubAgentToolParams | any;
      results?: SpawnSubAgentToolResults | any;
    }
  | {
      tool: ChatToolType | string;
      toolName?: string | null;
      params: any;
      results: { error: string | { message: string } };
    }) => {
  const { tool, toolName, params, results } = props;
  if (!!results && "error" in results) {
    const errorMessage =
      typeof results.error === "string"
        ? results.error
        : results.error?.message || "Tool execution failed";
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: "0.75rem",
          color: "red",
        }}
      >
        <ToolIcon /> <span>{errorMessage}</span>
      </Box>
    );
  }
  switch (tool) {
    case ChatToolType.DocumentsRagTool:
      return <DocumentsRagTool params={params} results={results} />;
    case ChatToolType.NotesRagTool:
      return <NotesRagTool params={params} results={results} />;
    case ChatToolType.CitationTool:
      if (!results) {
        return (
          <div>
            Missing citation results, this should never happen, an error should
            be displayed.
          </div>
        );
      }
      return (
        <CitationTool
          {...{ params, results: results as CitationToolResults }}
        />
      );
    case ChatToolType.CurrentPageTool:
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.75rem",
            color: "gray",
          }}
        >
          <ToolIcon /> {" Current Page Tool"}
        </Box>
      );
    case ChatToolType.CsvExportTool:
      if (!results) {
        return (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: "0.75rem",
              color: "gray",
            }}
          >
            <ToolIcon /> Generating CSV export...
          </Box>
        );
      }
      return (
        <CsvExportTool {...{ results: results as CsvExportToolResults }} />
      );
    case ChatToolType.AlphaVantageMcp:
      return <AlphaVantageMcpTool toolName={toolName} results={results} />;
    case ChatToolType.SiriusMcp:
      return (
        <SiriusMcpTool toolName={toolName} params={params} results={results} />
      );
    case ChatToolType.UpdatePreferencesTool:
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.75rem",
            color: "#8B5CF6",
          }}
        >
          <BulbOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
          Preferences updated
        </Box>
      );
    case ChatToolType.DocumentTextReaderTool:
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.75rem",
            color: "gray",
          }}
        >
          <FileTextOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
          Reading: {params?.fileName}
          {params?.mode && params.mode !== "read" && ` (${params.mode})`}
        </Box>
      );
    case ChatToolType.SpreadsheetReaderTool:
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.75rem",
            color: "gray",
          }}
        >
          <TableOutlined style={{ fontSize: "0.875rem", marginRight: 4 }} />
          Spreadsheet: {params?.fileName}
          {params?.sheetName && ` \u2014 ${params.sheetName}`}
        </Box>
      );
    case ChatToolType.CalculatorTool:
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.75rem",
            color: "gray",
          }}
        >
          <ToolIcon /> Calculator
          {params?.expression && `: ${params.expression}`}
        </Box>
      );

    case ChatToolType.AtlasTool:
      return <AtlasToolDisplay params={params} results={results} />;

    case ChatToolType.ActivateSkillTool:
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.75rem",
            color: "gray",
          }}
        >
          <ToolIcon /> Activating skill
          {params?.skillName && `: ${params.skillName}`}
        </Box>
      );

    case ChatToolType.SpawnSubAgentTool:
      // If toolName is set, this is a sub-agent's internal tool call — render as MCP tool
      if (toolName) {
        return <SiriusMcpTool toolName={toolName} params={params} results={results} />;
      }
      return <SpawnSubAgentToolDisplay params={params} results={results} />;

    case ChatToolType.StageMutationsTool:
      return <StageMutationsToolDisplay params={params} results={results} />;

    case ChatToolType.ModifyMutationPlanTool:
      return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.75rem", color: "gray" }}>
          <ToolIcon /> Edit Staged Data Updates <BetaChip />
        </Box>
      );

    default:
      captureMessage(`Unknown tool type: ${String(tool)}`, {
        level: "warning",
        extra: { tool, toolName, params, results },
      });
      return (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "0.75rem",
            color: "gray",
          }}
        >
          <ToolIcon />
          <span>Tool: {String(tool)}</span>
        </Box>
      );
  }
};

// ---------------------------------------------------------------------------
// Collapsible wrapper for tool calls
// ---------------------------------------------------------------------------

const toolShortLabel = (tool: string, toolName?: string | null): string => {
  if (tool === ChatToolType.SiriusMcp) return mcpLabel(toolName);
  if (tool === ChatToolType.AlphaVantageMcp)
    return toolName ? formatAvToolName(toolName) : "Market Data";
  switch (tool) {
    case ChatToolType.DocumentsRagTool: return "Searching documents";
    case ChatToolType.NotesRagTool: return "Searching notes";
    case ChatToolType.CurrentPageTool: return "Reading page";
    case ChatToolType.CsvExportTool: return "Exporting CSV";
    case ChatToolType.UpdatePreferencesTool: return "Updating preferences";
    case ChatToolType.DocumentTextReaderTool: return "Reading document";
    case ChatToolType.SpreadsheetReaderTool: return "Reading spreadsheet";
    case ChatToolType.CalculatorTool: return "Calculating";
    case ChatToolType.AtlasTool: return "Running code";
    case ChatToolType.CitationTool: return "Citing";
    case ChatToolType.SpawnSubAgentTool: return "Sub-agent";
    case ChatToolType.StageMutationsTool: return "Staging plan";
    case ChatToolType.ModifyMutationPlanTool: return "Modifying plan";
    case ChatToolType.ActivateSkillTool: return "Activating skill";
    default: return tool;
  }
};

const MAX_VISIBLE_TOOL_LABELS = 2;

const toolCallsLabel = (tools: { tool: string; toolName?: string | null }[]): string => {
  const labels = tools.slice(0, MAX_VISIBLE_TOOL_LABELS).map(t => toolShortLabel(t.tool, t.toolName));
  const remainder = tools.length - MAX_VISIBLE_TOOL_LABELS;
  return remainder > 0 ? `${labels.join(" · ")} +${remainder} more` : labels.join(" · ");
};

/**
 * Wraps a list of tool call displays in a collapsed-by-default container.
 * Shows a short summary line; click to expand and see individual tool details.
 */
export const CollapsibleToolCalls = ({
  children,
  toolCount,
  tools,
}: {
  children: React.ReactNode;
  toolCount: number;
  tools: { tool: string; toolName?: string | null }[];
}) => {
  const [expanded, setExpanded] = useState(false);
  const label = toolCallsLabel(tools);

  return (
    <Box sx={{ my: 0.5 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} ${toolCount} tool ${toolCount !== 1 ? "calls" : "call"}`}
        data-cy="collapsible-tool-calls-toggle"
        onClick={() => setExpanded(prev => !prev)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(prev => !prev); } }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          fontSize: "0.75rem",
          color: "gray",
          userSelect: "none",
          "&:hover": { color: "#333" },
        }}
      >
        {expanded ? (
          <DownOutlined style={{ fontSize: "0.5rem" }} />
        ) : (
          <RightOutlined style={{ fontSize: "0.5rem" }} />
        )}
        <ToolOutlined style={{ fontSize: "0.75rem" }} />
        {label}
      </Box>
      {expanded && <Box sx={{ mt: 0.5, ml: 1.5 }}>{children}</Box>}
    </Box>
  );
};

/**
 * Top-level display for sub-agent activity. Rendered alongside CollapsibleToolCalls
 * to give sub-agents first-class visibility in the chat.
 */
export const CollapsibleSubAgents = ({
  subAgentTools,
}: {
  subAgentTools: { toolId: string; params: any; results?: any }[];
}) => {
  const [expanded, setExpanded] = useState(true);
  const pendingCount = subAgentTools.filter(t => !t.results).length;
  const doneCount = subAgentTools.length - pendingCount;

  const statusLabel = pendingCount > 0
    ? `${pendingCount} running${doneCount > 0 ? `, ${doneCount} done` : ""}`
    : `${doneCount} completed`;

  return (
    <Box sx={{ my: 0.5 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? "Collapse" : "Expand"} ${subAgentTools.length} sub-agent${subAgentTools.length !== 1 ? "s" : ""}`}
        onClick={() => setExpanded(prev => !prev)}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(prev => !prev); } }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          fontSize: "0.75rem",
          color: pendingCount > 0 ? "#1976d2" : "#2e7d32",
          transition: "color 0.3s ease",
          userSelect: "none",
          "&:hover": { color: "#333" },
        }}
      >
        {expanded ? (
          <DownOutlined style={{ fontSize: "0.5rem" }} />
        ) : (
          <RightOutlined style={{ fontSize: "0.5rem" }} />
        )}
        <ApiOutlined style={{ fontSize: "0.75rem" }} />
        <span>
          {subAgentTools.length} sub-agent{subAgentTools.length !== 1 ? "s" : ""}{" "}
          <span style={{ fontWeight: 400 }}>({statusLabel})</span>
        </span>
      </Box>
      {expanded && (
        <Box sx={{ mt: 0.5, ml: 1.5 }}>
          {subAgentTools.map(({ toolId, params, results }) => (
            <SpawnSubAgentToolDisplay
              key={toolId}
              params={params}
              results={results}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * Renders a tools-display block: CollapsibleToolCalls for regular tools and
 * CollapsibleSubAgents for sub-agent spawns. Both the live subscription
 * stream (streamEventToHistory) and the LOAD_CONVERSATION restore path
 * (ConversationHistoryTab.handleClick) build this same shape — extracted
 * here so the JSX lives in one place.
 *
 * Callers control which tools are passed in:
 * - Live: pre-filtered to exclude internal sub-agent tools (those get
 *   merged into their parent spawn's results inline) and chart tools (which
 *   render via inline {{chart:}} markers in the markdown body).
 * - Restore: passes the raw tools array from the saved chatContext; the
 *   component itself partitions sub-agent spawns from regular tools.
 */
export function ToolsBlock({
  tools,
  keyPrefix,
}: {
  tools: { toolId: string; tool: string; toolName?: string | null; params: any; results: any }[];
  /** Prefix for ToolDisplays React keys when toolId is missing (saved-history items). */
  keyPrefix?: string;
}): React.ReactElement | null {
  const subAgentSpawns = tools.filter(
    t => t.tool === ChatToolType.SpawnSubAgentTool && !t.toolName
  );
  const regularTools = tools.filter(t => t.tool !== ChatToolType.SpawnSubAgentTool);
  // Out of the collapsed list, like sub-agents: the Download link is the
  // result the user is waiting for, not a detail of how the answer was made.
  const reportCalls = regularTools.filter(isDiligenceReportCall);
  if (regularTools.length === 0 && subAgentSpawns.length === 0) return null;
  return (
    <>
      {regularTools.length > 0 && (
        <CollapsibleToolCalls toolCount={regularTools.length} tools={regularTools}>
          {regularTools.map(({ toolId, tool, toolName, params, results }, idx) => (
            <ToolDisplays
              key={toolId || `${keyPrefix ?? "tool"}-${tool}-${idx}`}
              tool={tool as ChatToolType}
              toolName={toolName}
              params={params}
              results={results}
            />
          ))}
        </CollapsibleToolCalls>
      )}
      {reportCalls.map(({ toolId, results }, idx) => (
        <Box key={toolId || `${keyPrefix ?? "report"}-${idx}`}>
          <DiligenceReportDisplay results={results} />
        </Box>
      ))}
      {subAgentSpawns.length > 0 && (
        <CollapsibleSubAgents subAgentTools={subAgentSpawns} />
      )}
    </>
  );
}
